import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'

import { EditIcon } from '../../../components/ui/EditIcon'
import { EmailIcon } from '../../../components/ui/EmailIcon'
import { SendIcon } from '../../../components/ui/SendIcon'
import { Spinner } from '../../../components/ui/Spinner'
import { colors } from '../../../theme'
import {
  body3,
  headline2,
  headlineFont,
  label1,
  label2,
} from '../../../theme/typography'
import {
  settingsButtonHeightClassName,
  SettingsCard,
  settingsEmailBodyTextStyle,
  settingsSectionTitleTextStyle,
} from './SettingsPrimitives'

type ProfileSettingsCardProps = {
  avatarInitial: string
  displayName: string
  userEmail: string
  isEmailVerified: boolean | undefined
  onOpenEditName: () => void
  onResendVerification: () => void
  isResendVerificationPending: boolean
  isAssertingValidUserEmail: boolean
  hasSentVerificationEmail: boolean
  onLogout: () => void
  isLogoutPending: boolean
}

export function ProfileSettingsCard({
  avatarInitial,
  displayName,
  userEmail,
  isEmailVerified,
  onOpenEditName,
  onResendVerification,
  isResendVerificationPending,
  isAssertingValidUserEmail,
  hasSentVerificationEmail,
  onLogout,
  isLogoutPending,
}: ProfileSettingsCardProps) {
  const { t } = useTranslation(['profile'])

  return (
    <SettingsCard className="mb-6 px-4 py-4">
      <Pressable
        className="flex-row items-center active:opacity-70"
        onPress={onOpenEditName}
      >
        <View className="mr-4 h-10 w-10 items-center justify-center rounded-full bg-primary">
          <Text
            style={[
              headline2,
              {
                color: colors.primaryForeground,
                fontFamily: headlineFont,
                fontSize: 20,
                width: 40,
                height: 40,
                lineHeight: 40,
                letterSpacing: 0,
                textAlign: 'center',
                textAlignVertical: 'center',
                includeFontPadding: false,
              },
            ]}
          >
            {avatarInitial || '?'}
          </Text>
        </View>
        <View className="min-w-0 flex-1">
          <View className="flex-row items-center">
            <Text
              style={[
                label1,
                {
                  color: colors.textHeader,
                  letterSpacing: 0,
                  flexShrink: 1,
                },
              ]}
              numberOfLines={1}
            >
              {displayName}
            </Text>
            <View className="ml-auto">
              <EditIcon />
            </View>
          </View>
          {userEmail ? (
            <Text
              style={[
                body3,
                {
                  color: colors.textBody,
                  marginTop: 2,
                  lineHeight: 19,
                  letterSpacing: 0,
                },
              ]}
              numberOfLines={1}
            >
              {userEmail}
            </Text>
          ) : null}
        </View>
      </Pressable>

      {!isEmailVerified ? (
        <View
          className="mt-4 rounded-lg px-4 py-4"
          style={{ backgroundColor: colors.secondary700 }}
        >
          <View className="flex-row items-center">
            <EmailIcon />
            <View className="flex-1 ml-1">
              <Text style={[label2, settingsSectionTitleTextStyle]}>
                {t('profile:email_not_verified_title')}
              </Text>
            </View>
          </View>
          <Text style={[body3, settingsEmailBodyTextStyle]}>
            {t('profile:email_not_verified_message')}
          </Text>

          {!hasSentVerificationEmail ? (
            <Pressable
              className={`mt-4 ${settingsButtonHeightClassName} items-center justify-center rounded-lg active:opacity-80`}
              style={{ backgroundColor: colors.primary }}
              onPress={onResendVerification}
              disabled={
                isResendVerificationPending || isAssertingValidUserEmail
              }
            >
              {isResendVerificationPending ? (
                <Spinner color={colors.primaryForeground} />
              ) : isAssertingValidUserEmail ? (
                <Text style={[label2, { color: colors.primaryForeground }]}>
                  {t('profile:waiting_for_verification')}
                </Text>
              ) : (
                <View className="flex-row items-center">
                  <View style={{ marginRight: 10 }}>
                    <SendIcon />
                  </View>
                  <Text
                    style={[
                      label2,
                      settingsSectionTitleTextStyle,
                      { color: colors.primaryForeground },
                    ]}
                  >
                    {t('profile:resend_verification_email')}
                  </Text>
                </View>
              )}
            </Pressable>
          ) : null}
        </View>
      ) : null}

      <Pressable
        className={`mt-4 ${settingsButtonHeightClassName} items-center justify-center rounded-lg active:bg-fill`}
        style={{
          borderWidth: 1.5,
          borderColor: colors.textHeader,
        }}
        onPress={onLogout}
        disabled={isLogoutPending}
      >
        {isLogoutPending ? (
          <Spinner color={colors.textBody} />
        ) : (
          <Text
            style={[
              label2,
              settingsSectionTitleTextStyle,
              { color: colors.textHeader },
            ]}
          >
            {t('profile:logout')}
          </Text>
        )}
      </Pressable>
    </SettingsCard>
  )
}
